/**
 * authConfig.js
 * MSAL configuration for H2 Fin Portal (apps.h2fin.com)
 *
 * SETUP REQUIRED:
 * 1. In Entra ID → App registrations → New registration:
 *    - Name: H2 Fin Portal
 *    - Supported account types: Single tenant (Harness Financial M365 tenant only)
 *    - Redirect URI: Single-page application → https://apps.h2fin.com
 *    - Also add: http://localhost:3000 for local dev
 *
 * 2. After registration, copy:
 *    - Application (client) ID → PORTAL_CLIENT_ID below
 *    - Directory (tenant) ID   → TENANT_ID below
 *
 * 3. Under Authentication → Implicit grant and hybrid flows:
 *    - Enable: Access tokens
 *    - Enable: ID tokens
 *
 * 4. Under Token configuration → Add groups claim:
 *    - Security groups → Group ID
 *    This populates the 'groups' claim used for role detection.
 *
 * 5. Under API permissions:
 *    - Microsoft Graph → User.Read (already granted by default)
 *    - Microsoft Graph → GroupMember.Read.All (for group-based role lookup)
 *    Grant admin consent for the tenant.
 *
 * NOTE: This is a SEPARATE registration from HarnessTrack's existing app registration.
 * The portal registration covers the portal shell itself. Individual tools may reuse
 * their own registrations or be added as additional redirect URIs here (Option B arch).
 */

// ─── Replace these before deploying ─────────────────────────────────────────
const TENANT_ID        = process.env.REACT_APP_TENANT_ID;
const PORTAL_CLIENT_ID = process.env.REACT_APP_PORTAL_CLIENT_ID;
// ─────────────────────────────────────────────────────────────────────────────

export const msalConfig = {
  auth: {
    clientId:    PORTAL_CLIENT_ID,
    authority:   `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,   // Resolves to correct URI in any environment
  },
  cache: {
    cacheLocation:       "sessionStorage", // sessionStorage keeps auth within the tab; use localStorage to persist across tabs
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (process.env.NODE_ENV === "development") {
          console.log(`[MSAL] ${message}`);
        }
      },
    },
  },
};

/**
 * Login request — scopes requested at sign-in.
 * openid, profile, email are implicit with OIDC.
 * User.Read allows the Graph /me endpoint for display name, email.
 */
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};

/**
 * Graph API endpoint used to fetch the signed-in user's group memberships,
 * which are mapped to portal roles in roleConfig.js.
 */
export const graphConfig = {
  graphMeEndpoint:     "https://graph.microsoft.com/v1.0/me",
  graphGroupsEndpoint: "https://graph.microsoft.com/v1.0/me/memberOf",
};
